/**
 * Mock data generators for Request entities.
 *
 * Security Note: Math.random() is used throughout this file for generating
 * test/mock data only. This file is excluded from production bundles and
 * is not used for any security-sensitive operations.
 * All Math.random() usages here are safe (NOSONAR).
 */
import { Request } from '../../core/models/request.model';
import {
  RequestStatus,
  RequestStatusLabels,
} from '../../core/enums/request-status.enum';

const countries = [
  'United States',
  'Canada',
  'United Kingdom',
  'Germany',
  'France',
  'Australia',
  'Japan',
];
const printSites = [
  'Site A - New York',
  'Site B - Los Angeles',
  'Site C - Chicago',
  'Site D - London',
  null,
];
const shippingMethods = [
  'FedEx Ground',
  'FedEx Express',
  'UPS Ground',
  'USPS Priority',
  'DHL International',
  'FedEx Ground, USPS Priority',
];
const users = [
  'john.doe@example.com',
  'jane.smith@example.com',
  'bob.wilson@example.com',
  'alice.johnson@example.com',
  'charlie.brown@example.com',
];

function randomItem<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function randomDate(start: Date, end: Date): string {
  const date = new Date(
    start.getTime() + Math.random() * (end.getTime() - start.getTime())
  );
  return date.toISOString();
}

function generateRequestNumber(index: number): string {
  const year = new Date().getFullYear();
  const paddedIndex = String(index).padStart(6, '0');
  return `REQ-${year}-${paddedIndex}`;
}

function generateArNumber(): string {
  const year = new Date().getFullYear();
  const random = Math.floor(Math.random() * 10000);
  return `AR-${year}-${String(random).padStart(4, '0')}`;
}

export function generateMockRequest(index: number): Request {
  const status = randomItem(
    Object.values(RequestStatus).filter(v => typeof v === 'number')
  ) as RequestStatus;
  const assignedTo = Math.random() > 0.3 ? randomItem(users) : null;

  return {
    id: crypto.randomUUID(),
    requestNumber: generateRequestNumber(index),
    status,
    statusDisplay: RequestStatusLabels[status],
    requestDate: randomDate(new Date('2025-01-01'), new Date()),
    arNumber: generateArNumber(),
    country: randomItem(countries),
    createdBy: randomItem(users),
    distributionCount: Math.floor(Math.random() * 20),
    documentCount: Math.floor(Math.random() * 50) + 1,
    isExpedited: Math.random() > 0.7,
    assignedTo,
    printSite: randomItem(printSites),
    isPrinted: Math.random() > 0.5,
    shippingMethods: randomItem(shippingMethods),
    requiresManualCheck: Math.random() > 0.8,
  };
}

export function generateMockRequests(count = 250): Request[] {
  return Array.from({ length: count }, (_, i) => generateMockRequest(i + 1));
}

// Pre-generated mock data for consistent testing
let cachedRequests: Request[] | null = null;

export function getMockRequests(): Request[] {
  cachedRequests ??= generateMockRequests(250);
  return cachedRequests;
}
